import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';
import MaskedParamsEditor from '../MaskedParamsEditor.jsx';

export default function DemandPartnersTab({ publisherId }) {
  const toast = useToast();
  const [partners, setPartners] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listPublisherDemandPartners(publisherId);
      setPartners(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load demand partners.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [publisherId]);

  async function toggleEnabled(partner) {
    try {
      await api.putPublisherDemandPartner(publisherId, partner.code, {
        enabled: !partner.enabled,
        params: partner.params || {},
      });
      toast.success(`${partner.name} ${!partner.enabled ? 'enabled' : 'disabled'} for this publisher.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update partner.');
    }
  }

  if (loading) return <LoadingState label="Loading demand partners" />;
  if (error) return <EmptyState title="Couldn't load demand partners" message={error} />;
  if (!partners || partners.length === 0) {
    return <EmptyState title="No demand partners configured" message="Enable partners network-wide from the Demand Partners section first." />;
  }

  return (
    <section className="card card--flush">
      <div className="card__header card__header--padded">
        <h2 className="card__title">Demand partner overrides</h2>
        <span className="card__hint">Per-publisher enable state and connector params</span>
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Partner</th>
              <th>Network status</th>
              <th>Publisher status</th>
              <th>Params</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.demand_partner_id}>
                <td>
                  <div className="cell-title">{p.name}</div>
                  <div className="cell-subtitle">{p.code}</div>
                </td>
                <td>
                  <StatusBadge status={p.partner_enabled ? 'active' : 'paused'} />
                </td>
                <td>
                  <label className="switch">
                    <input type="checkbox" checked={!!p.enabled} onChange={() => toggleEnabled(p)} />
                    <span className="switch__track">
                      <span className="switch__thumb" />
                    </span>
                  </label>
                </td>
                <td className="cell-muted">{Object.keys(p.params || {}).length} param(s)</td>
                <td>
                  <button className="btn btn--ghost btn--sm" onClick={() => setEditing(p)}>
                    Edit params
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ParamsModal
          title={`${editing.name} — publisher params`}
          existingKeys={Object.keys(editing.params || {})}
          onClose={() => setEditing(null)}
          onSave={async (params) => {
            // Race-condition fix: don't send editing.enabled -- it's a snapshot captured at
            // "Edit params" click time. If the enable/disable switch (a separate control, see
            // toggleEnabled above) was flipped after this modal opened but before this save
            // fires, editing.enabled is stale and this PUT (a full replace, not a merge) would
            // silently revert the just-changed enabled state back to its pre-toggle value.
            // `partners` is refreshed by every load() and re-read fresh here on every render, so
            // look up the CURRENT enabled state for this partner at save time instead.
            const current = partners.find((p) => p.code === editing.code);
            await api.putPublisherDemandPartner(publisherId, editing.code, {
              enabled: current ? current.enabled : editing.enabled,
              params,
            });
            toast.success('Credentials updated.');
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

// Credential hygiene: never re-displays a saved value (see MaskedParamsEditor's own header) --
// `existingKeys` is only the key NAMES already saved for this publisher/partner pair, sourced
// from the same GET this tab already loaded (that route's plaintext contract is unchanged and
// still tested; this modal simply chooses not to render those values).
export function ParamsModal({ title, existingKeys, onClose, onSave }) {
  const toast = useToast();

  async function handleSave(params) {
    try {
      await onSave(params);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save params.');
      throw err;
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <MaskedParamsEditor existingKeys={existingKeys} onSave={handleSave} />
      <div className="form__actions">
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
